import type { AddonPhoto, AddonPhotoVariant } from "./types";

/**
 * Every size the server can offer, ordered cheapest-first.
 *
 * A missing variant is not an error to surface — the reader wants to see the
 * add-on, not a broken image — so a request for one size falls through to the
 * others rather than returning nothing.
 */
const FALLBACK_ORDER: readonly AddonPhotoVariant[] = [
  "card",
  "original",
  "thumb",
];

/** Photos with no position sort after the ones that have one, in server order. */
const NO_POSITION = Number.MAX_SAFE_INTEGER;

function firstUsableUrl(
  photo: AddonPhoto,
  preferred: AddonPhotoVariant,
): string | null {
  const order = [preferred, ...FALLBACK_ORDER.filter((v) => v !== preferred)];

  for (const variant of order) {
    const url = photo.attachment_urls?.[variant];
    if (url) return url;
  }

  return null;
}

/**
 * `toSorted` keeps the caller's array untouched; the sort is stable, so
 * photos sharing a position stay in the order the server sent them.
 */
function sortedByPosition(photos: AddonPhoto[]): AddonPhoto[] {
  return [...photos].sort(
    (a, b) => (a.position ?? NO_POSITION) - (b.position ?? NO_POSITION),
  );
}

/**
 * The URL to show for an add-on at the given size, or `null` when it has no
 * usable photo.
 *
 * Callers pass the size the surface actually needs — `thumb` for a table cell,
 * `card` for a checkout tile — so a storefront never downloads a multi-megabyte
 * original to paint a 400px box.
 */
export function addonPhotoUrl(
  addon: { photos?: AddonPhoto[] },
  variant: AddonPhotoVariant,
): string | null {
  const photos = addon.photos ?? [];
  if (photos.length === 0) return null;

  for (const photo of sortedByPosition(photos)) {
    const url = firstUsableUrl(photo, variant);
    if (url) return url;
  }

  return null;
}

/**
 * Every photo for an add-on, ordered by `position`, with a usable URL at
 * the given size. A photo whose upload never produced a URL for any size is
 * dropped rather than shown broken. `[]` when there are none.
 *
 * The add-on's own `name` is used as `alt` for every image: `photo.caption`
 * is always `null` in practice today (see design notes), so it carries no
 * usable text.
 */
export function addonGalleryImages(
  addon: { name: string; photos?: AddonPhoto[] },
  variant: AddonPhotoVariant,
): Array<{ id: string; url: string; alt: string }> {
  const photos = addon.photos ?? [];
  if (photos.length === 0) return [];

  const images: Array<{ id: string; url: string; alt: string }> = [];
  for (const photo of sortedByPosition(photos)) {
    const url = firstUsableUrl(photo, variant);
    if (url) images.push({ id: photo.id, url, alt: addon.name });
  }

  return images;
}
